import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { Button, HasPermissions, UserNameLink } from "components";
import { getFullName, permissionList, useGetUserUrl } from "helpers";
import {
	useUnderwritingClaimMutation,
	useUnderwritingUnclaimMutation,
} from "queries";
import { merchantInfoActions, uiActions } from "store";
import { getCurrentUserId } from "store/user/user.reducer";
import { UnderwritingTabs, UserType } from "types";

import styles from "./underwritingNavInfo.module.scss";

type AssigneeItemProps = {
	assignee: UserType;
	applicationId: string;
	isLoading: boolean;
	refetchApplicationInfo: Function;
};

const AssignItem = ({
	assignee,
	applicationId,
	isLoading,
	refetchApplicationInfo,
}: AssigneeItemProps) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const currentUserId = useSelector(getCurrentUserId);
	const { tab, underwritingTab } = useParams<any>();

	const assigneeName = assignee ? getFullName(assignee) : "Unassigned";
	const assigneeUrl = useGetUserUrl(assignee);
	const isAssignToUser = currentUserId === assignee?.id;
	const isAppHasAssignee = !!assignee?.id;

	const { mutate: handleClaim, isLoading: isLoadingHandleClaim } =
		useUnderwritingClaimMutation();

	const { mutate: handleUnclaim, isLoading: isLoadingHandleUnclaim } =
		useUnderwritingUnclaimMutation();

	const onClaimClick = useCallback(() => {
		refetchApplicationInfo();
		if (isAssignToUser) {
			handleUnclaim?.({
				id: applicationId,
				onSuccessHandler: () => {
					if (tab === UnderwritingTabs.InReview.name) {
						history.push(
							`/underwriting/unassigned/${applicationId}/${underwritingTab}`,
						);
					} else {
						dispatch(
							merchantInfoActions.getMerchantInformation(
								applicationId,
								tab,
							),
						);
					}
				},
			});
		} else {
			if (isAppHasAssignee) {
				dispatch(
					uiActions.showModal("CONFIRMATION", {
						title: [
							`This is already assigned to ${assigneeName}. Are you sure you want to claim it?`,
						],
						onConfirm() {
							handleClaim?.({
								id: applicationId,
								onSuccessHandler: () => {
									if (
										tab === UnderwritingTabs.Unassigned.name
									) {
										history.push(
											`/underwriting/in-review/${applicationId}/${underwritingTab}`,
										);
									} else {
										dispatch(
											merchantInfoActions.getMerchantInformation(
												applicationId,
												tab,
											),
										);
									}
								},
							});
						},
					}),
				);
			} else {
				handleClaim?.({
					id: applicationId,
					onSuccessHandler: () => {
						if (tab === UnderwritingTabs.Unassigned.name) {
							history.push(
								`/underwriting/in-review/${applicationId}/${underwritingTab}`,
							);
						} else {
							dispatch(
								merchantInfoActions.getMerchantInformation(
									applicationId,
									tab,
								),
							);
						}
					},
				});
			}
		}
	}, [
		applicationId,
		assigneeName,
		dispatch,
		handleClaim,
		handleUnclaim,
		history,
		isAppHasAssignee,
		isAssignToUser,
		refetchApplicationInfo,
		tab,
		underwritingTab,
	]);

	return (
		<div className={styles.assigneeHolder}>
			<UserNameLink
				fullName={assigneeName}
				url={assigneeUrl}
				isLoading={isLoading}
			/>

			<HasPermissions
				requiredPermissions={
					permissionList.UnderwritingSensitiveCreditInfo
				}
			>
				<Button
					className={styles.assigneeButton}
					minWidth="auto"
					variant="secondary"
					value={isAssignToUser ? "unclaim" : "claim"}
					onClick={onClaimClick}
					disabled={isLoadingHandleClaim || isLoadingHandleUnclaim}
				/>
			</HasPermissions>
		</div>
	);
};

export default AssignItem;
